/** A 16px box with an accent edge, for choosing a token's scopes.
 *
 * The one place the portal uses a checkbox is also the one place where the
 * choice is load-bearing: the scopes ticked here are what an installation is
 * allowed to send for the life of the token. So the input stays a real
 * `<input type="checkbox">`, hidden but focusable, with the drawn box as its
 * sibling — the keyboard, the form and assistive technology all get an
 * ordinary checkbox, and the ticked state is painted rather than themed.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';

export interface CheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Usually the scope itself, in the monospace face. */
  label: ReactNode;
  /** One line saying what that scope permits. */
  description?: ReactNode;
  disabled?: boolean;
  name?: string;
  value?: string;
  className?: string;
}

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled,
  name,
  value,
  className,
}: CheckboxProps) {
  const descriptionId = useId();

  return (
    <label
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: '16px minmax(0,1fr)',
        gap: 10,
        alignItems: 'start',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13.5,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <span style={{ position: 'relative', display: 'inline-flex', marginTop: 2 }}>
        <input
          type="checkbox"
          className="sr-input"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          aria-describedby={description ? descriptionId : undefined}
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
        {/* Drawn rather than themed: the tick is the accent's own shape, and
            the focus ring is taken from the input by `.sr-input:focus-visible`. */}
        <span
          aria-hidden="true"
          className="check-mark"
          style={{ background: checked ? 'var(--color-accent)' : 'transparent' }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="var(--color-bg)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ opacity: checked ? 1 : 0 }}
          >
            <path d="M1.5 5.2l2.3 2.3 4.7-5" />
          </svg>
        </span>
      </span>

      <span style={{ minWidth: 0 }}>
        {label}
        {description ? (
          <span
            id={descriptionId}
            style={{
              display: 'block',
              fontSize: 11.5,
              color: 'var(--color-neutral-500)',
              textWrap: 'pretty',
            }}
          >
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export default Checkbox;
